import { useState, type ReactNode } from "react";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import { Button } from "./button";
import { Input } from "./input";
import { Field } from "./field";
import { cn } from "@/lib/utils";

/**
 * Center-mount dialog — for short flows (rename, edit dates, confirm delete).
 * Long / multi-step flows use the bottom Sheet instead.
 */

export function Dialog({
  open,
  onOpenChange,
  children,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  children: ReactNode;
}) {
  return (
    <DialogPrimitive.Root open={open} onOpenChange={onOpenChange}>
      {children}
    </DialogPrimitive.Root>
  );
}

export function DialogContent({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-40 bg-[rgba(20,15,10,0.55)] backdrop-blur-sm data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=open]:fade-in-0 data-[state=closed]:fade-out-0" />
      <DialogPrimitive.Content
        className={cn(
          "fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2",
          "rounded-xl bg-bg-elevated shadow-warm-3 border border-line",
          "p-6",
          className,
        )}
      >
        {children}
        <DialogPrimitive.Close
          aria-label="Close"
          className="absolute right-3 top-3 flex h-8 w-8 items-center justify-center rounded-full text-ink-subtle hover:bg-bg-sunken hover:text-ink"
        >
          <X className="h-4 w-4" />
        </DialogPrimitive.Close>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

export function DialogHeader({
  title,
  description,
}: {
  title: string;
  description?: string;
}) {
  return (
    <div className="mb-4 pr-6">
      <DialogPrimitive.Title asChild>
        <h2
          className="m-0 font-display text-2xl leading-tight tracking-tight text-ink"
          style={{ fontVariationSettings: `"opsz" 48, "wght" 500` }}
        >
          {title}
        </h2>
      </DialogPrimitive.Title>
      {description ? (
        <DialogPrimitive.Description asChild>
          <p className="mt-1 text-[14px] text-ink-muted m-0">{description}</p>
        </DialogPrimitive.Description>
      ) : (
        <DialogPrimitive.Description className="sr-only">{title}</DialogPrimitive.Description>
      )}
    </div>
  );
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">{children}</div>;
}

// ─── ConfirmDialog — short-hand for a two-button "sure?" ─────
interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  title: string;
  description?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  destructive?: boolean;
  /** Async handler — the confirm button shows a loading state while it runs. */
  onConfirm: () => Promise<void> | void;
  /** Type-to-confirm string. When set, confirm button disables until input matches. */
  typeToConfirm?: string;
}

export function ConfirmDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  destructive = false,
  onConfirm,
  typeToConfirm,
}: ConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader title={title} description={description} />
        {typeToConfirm ? (
          <TypeToConfirmField expected={typeToConfirm} onConfirm={onConfirm} confirmLabel={confirmLabel} cancelLabel={cancelLabel} destructive={destructive} onCancel={() => onOpenChange(false)} />
        ) : (
          <DialogFooter>
            <Button variant="secondary" onClick={() => onOpenChange(false)}>
              {cancelLabel}
            </Button>
            <Button
              variant={destructive ? "destructive" : "primary"}
              onClick={async () => {
                await onConfirm();
              }}
            >
              {confirmLabel}
            </Button>
          </DialogFooter>
        )}
      </DialogContent>
    </Dialog>
  );
}

// Type-to-confirm sub-form — colocated to keep the API simple.
function TypeToConfirmField({
  expected,
  onConfirm,
  onCancel,
  confirmLabel,
  cancelLabel,
  destructive,
}: {
  expected: string;
  onConfirm: () => Promise<void> | void;
  onCancel: () => void;
  confirmLabel: string;
  cancelLabel: string;
  destructive: boolean;
}) {
  const [value, setValue] = useState("");
  const [busy, setBusy] = useState(false);
  const matches = value.trim() === expected.trim();

  return (
    <div className="flex flex-col gap-4">
      <Field
        id="confirm-name"
        label={`Type "${expected}" to confirm`}
      >
        <Input
          id="confirm-name"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          autoFocus
          autoComplete="off"
          spellCheck={false}
        />
      </Field>
      <DialogFooter>
        <Button variant="secondary" onClick={onCancel} disabled={busy}>
          {cancelLabel}
        </Button>
        <Button
          variant={destructive ? "destructive" : "primary"}
          disabled={!matches || busy}
          onClick={async () => {
            setBusy(true);
            try {
              await onConfirm();
            } finally {
              setBusy(false);
            }
          }}
        >
          {busy ? "Working…" : confirmLabel}
        </Button>
      </DialogFooter>
    </div>
  );
}
